import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getCardsByPack, getPackById } from "../db";
import { deleteCard, putCard } from "./adminApi";
import type { Card, Pack } from "../domain/types";
import { Button } from "../components/Button";

type FilterId = "all" | "main" | "reward" | "penalty" | "trigger";

const PHASE_ORDER: Record<string, number> = { warmup: 0, engagement: 1, peak: 2 };
const KIND_ORDER: Record<string, number> = { main: 0, reward: 1, penalty: 2 };

export default function AdminCards() {
  const { packId } = useParams<{ packId: string }>();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pack, setPack] = useState<Pack | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const filterRaw = searchParams.get("filter");
  const filter: FilterId =
    filterRaw === "main" || filterRaw === "reward" || filterRaw === "penalty" || filterRaw === "trigger"
      ? filterRaw
      : "all";
  const setFilter = (f: FilterId) => {
    const next = new URLSearchParams(searchParams);
    if (f === "all") next.delete("filter");
    else next.set("filter", f);
    setSearchParams(next, { replace: true });
  };

  async function refresh() {
    if (!packId) return;
    const p = (await getPackById(packId)) ?? null;
    setPack(p);
    setCards(await getCardsByPack(packId));
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packId]);

  /**
   * One-click demo seed: creates a main card with bound 🏆 + 💀 inside the
   * current pack so the host can verify the bound-consequence pipeline
   * without typing anything in. Each click makes a fresh trio (random
   * baseId), so it's safe to click multiple times.
   */
  async function seedDemoTriple() {
    if (!packId || !pack) return;
    const baseId = `demo_${Date.now().toString(36)}_${Math.random()
      .toString(36)
      .slice(2, 6)}`;
    const childRewardBase = `${baseId}_bound_reward`;
    const childPenaltyBase = `${baseId}_bound_penalty`;
    const common = {
      packId,
      modeId: pack.modeId,
      ageGate: pack.isAdult,
      interactionMode: null as Card["interactionMode"],
      answerTimeSec: null,
      reactionWindowSec: null,
    };
    const main: Card = {
      ...common,
      id: `uk:${baseId}`,
      language: "uk",
      type: "task",
      phase: "warmup",
      text:
        "🪄 ТЕСТ: покажи мімікою найважливіший момент свого дня без слів. Гурт вгадує, що це.",
      intensity: 2,
      energy: 3,
      socialBoldness: 2,
      durationSec: null,
      hasTimer: false,
      involvesOthers: false,
      participantsCount: null,
      kind: "main",
      triggersConsequence: true,
      suppressReward: false,
      suppressPenalty: false,
    };
    const boundReward: Card = {
      ...common,
      id: `uk:${childRewardBase}`,
      language: "uk",
      type: "task",
      phase: "warmup",
      text:
        "🏆 ТЕСТ-нагорода: усі плескають у долоні три рази й кричать «Браво!»",
      intensity: 2,
      energy: 3,
      socialBoldness: 2,
      durationSec: null,
      hasTimer: false,
      involvesOthers: true,
      participantsCount: "all",
      kind: "reward",
      triggersConsequence: false,
      parentCardId: baseId,
    };
    const boundPenalty: Card = {
      ...common,
      id: `uk:${childPenaltyBase}`,
      language: "uk",
      type: "mini_challenge",
      phase: "warmup",
      text:
        "💀 ТЕСТ-покарання: 10 присідань на час. Поки не закінчиш — не граєш.",
      intensity: 2,
      energy: 3,
      socialBoldness: 2,
      durationSec: 30,
      hasTimer: true,
      involvesOthers: false,
      participantsCount: 0,
      kind: "penalty",
      triggersConsequence: false,
      parentCardId: baseId,
    };
    await putCard(main);
    await putCard(boundReward);
    await putCard(boundPenalty);
    await refresh();
  }

  const groupedTriples = useMemo(() => {
    const grouped = new Map<string, { uk?: Card; ru?: Card; en?: Card }>();
    for (const c of cards) {
      if (c.parentCardId) continue;
      const baseKey = c.id.replace(/^(uk|ru|en):/, "");
      const g = grouped.get(baseKey) ?? {};
      g[c.language] = c;
      grouped.set(baseKey, g);
    }
    return Array.from(grouped.entries());
  }, [cards]);

  const boundParentIds = useMemo(() => {
    const set = new Set<string>();
    for (const c of cards) {
      if (c.parentCardId) set.add(c.parentCardId);
    }
    return set;
  }, [cards]);

  const counts = useMemo(() => {
    let main = 0, reward = 0, penalty = 0, trigger = 0;
    for (const [, g] of groupedTriples) {
      const sample = g.uk ?? g.ru ?? g.en;
      if (!sample) continue;
      const k = sample.kind ?? "main";
      if (k === "main") main++;
      else if (k === "reward") reward++;
      else if (k === "penalty") penalty++;
      if (sample.triggersConsequence) trigger++;
    }
    return { all: groupedTriples.length, main, reward, penalty, trigger };
  }, [groupedTriples]);

  const visibleTriples = useMemo(() => {
    const arr = groupedTriples
      .filter(([, g]) => {
        const sample = g.uk ?? g.ru ?? g.en;
        if (!sample) return false;
        const kind = sample.kind ?? "main";
        switch (filter) {
          case "all": return true;
          case "main": return kind === "main";
          case "reward": return kind === "reward";
          case "penalty": return kind === "penalty";
          case "trigger": return !!sample.triggersConsequence;
        }
      })
      .sort(([, a], [, b]) => {
        const A = a.uk ?? a.ru ?? a.en!;
        const B = b.uk ?? b.ru ?? b.en!;
        const ak = KIND_ORDER[A.kind ?? "main"] ?? 0;
        const bk = KIND_ORDER[B.kind ?? "main"] ?? 0;
        if (ak !== bk) return ak - bk;
        const ap = PHASE_ORDER[A.phase] ?? 0;
        const bp = PHASE_ORDER[B.phase] ?? 0;
        if (ap !== bp) return ap - bp;
        return (A.text ?? "").localeCompare(B.text ?? "", "uk");
      });
    return arr;
  }, [groupedTriples, filter]);

  if (!packId) return null;

  return (
    <div className="flex flex-col gap-3 pb-6">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => nav("/admin")}>
          ←
        </Button>
        <h3 className="font-display text-lg font-bold flex-1">{pack?.titleByLang.uk}</h3>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Link to={`/admin/pack/${packId}/new?kind=main&back=main`}>
          <Button size="sm" fullWidth>+ Картка</Button>
        </Link>
        <Link to={`/admin/pack/${packId}/new?kind=reward&back=reward`}>
          <Button size="sm" variant="ghost" fullWidth>+ 🏆</Button>
        </Link>
        <Link to={`/admin/pack/${packId}/new?kind=penalty&back=penalty`}>
          <Button size="sm" variant="ghost" fullWidth>+ 💀</Button>
        </Link>
      </div>

      {/* Demo seed — one click creates main + bound 🏆 + bound 💀 inside
          THIS pack. Lets the host eyeball the engine without typing. */}
      <button
        type="button"
        onClick={seedDemoTriple}
        className="text-xs px-3 py-2 rounded-lg bg-fuchsia-500/15 hover:bg-fuchsia-500/25 border border-fuchsia-400/30 text-fuchsia-100 transition"
        title="Створює одну картку із власною нагородою та власним покаранням, привʼязаними саме до неї"
      >
        🪄 Додати тестову трійцю (1 картка + 🏆 + 💀, привʼязані)
      </button>

      <div className="flex flex-wrap gap-1.5 text-xs">
        <Chip label="Усі" count={counts.all} active={filter === "all"} onClick={() => setFilter("all")} />
        <Chip label="Картки" count={counts.main} active={filter === "main"} onClick={() => setFilter("main")} />
        <Chip label="🏆 Нагороди" count={counts.reward} active={filter === "reward"} onClick={() => setFilter("reward")} accent="amber" />
        <Chip label="💀 Покарання" count={counts.penalty} active={filter === "penalty"} onClick={() => setFilter("penalty")} accent="red" />
        <Chip label="⚡ Тригери" count={counts.trigger} active={filter === "trigger"} onClick={() => setFilter("trigger")} accent="fuchsia" />
      </div>

      {visibleTriples.length === 0 && (
        <div className="text-sm opacity-60 italic mt-4">
          {groupedTriples.length === 0
            ? "Поки що немає карток — натисни + Картка щоб створити."
            : "Нічого не знайдено для цього фільтра."}
        </div>
      )}

      <ul className="flex flex-col gap-2">
        {visibleTriples.map(([baseId, g]) => {
          const sample = g.uk ?? g.ru ?? g.en;
          if (!sample) return null;
          return (
            <li key={baseId} className="rounded-xl bg-white/5 border border-white/10 p-3">
              <div className="flex items-start gap-2 flex-wrap">
                {sample.kind === "reward" && (
                  <span className="text-[10px] uppercase tracking-wider rounded bg-amber-400/20 text-amber-100 border border-amber-400/40 px-1.5 py-0.5">
                    🏆 reward
                  </span>
                )}
                {sample.kind === "penalty" && (
                  <span className="text-[10px] uppercase tracking-wider rounded bg-red-500/20 text-red-100 border border-red-400/40 px-1.5 py-0.5">
                    💀 penalty
                  </span>
                )}
                <span className="text-[10px] uppercase tracking-wider rounded bg-white/10 px-1.5 py-0.5">
                  {sample.type}
                </span>
                <span className="text-[10px] uppercase tracking-wider rounded bg-white/10 px-1.5 py-0.5">
                  {sample.phase}
                </span>
                {sample.triggersConsequence && (
                  <span
                    className="text-[10px] uppercase tracking-wider rounded bg-fuchsia-500/20 text-fuchsia-100 border border-fuchsia-400/40 px-1.5 py-0.5"
                    title="DONE → 🏆, FAILED → 💀"
                  >
                    ⚡ trigger
                  </span>
                )}
                {boundParentIds.has(baseId) && (
                  <span
                    className="text-[10px] uppercase tracking-wider rounded bg-emerald-500/20 text-emerald-100 border border-emerald-400/40 px-1.5 py-0.5"
                    title="Має власні 🏆 / 💀 (привʼязані тільки до цієї картки)"
                  >
                    🔗 bound
                  </span>
                )}
                <div className="flex-1" />
                <Link
                  to={`/admin/pack/${packId}/edit/${baseId}?back=${filter}`}
                  className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20"
                >
                  edit
                </Link>
                <button
                  className="text-xs px-2 py-1 rounded bg-red-500/30 hover:bg-red-500/50 text-red-100"
                  onClick={async () => {
                    if (!confirm("Видалити цю картку у всіх мовах?")) return;
                    if (g.uk) await deleteCard(g.uk.id);
                    if (g.ru) await deleteCard(g.ru.id);
                    if (g.en) await deleteCard(g.en.id);
                    await refresh();
                  }}
                >
                  ×
                </button>
              </div>
              <div className="mt-2 text-sm space-y-1">
                <Row tag="UK" text={g.uk?.text} />
                <Row tag="RU" text={g.ru?.text} />
                <Row tag="EN" text={g.en?.text} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Row({ tag, text }: { tag: string; text: string | undefined }) {
  return (
    <div className="flex gap-2">
      <span className="text-[10px] opacity-60 mt-0.5 w-6">{tag}</span>
      <span className={text ? "" : "italic opacity-40"}>{text || "(missing)"}</span>
    </div>
  );
}

function Chip({
  label,
  count,
  active,
  onClick,
  accent,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  accent?: "amber" | "red" | "fuchsia";
}) {
  const accentBg =
    accent === "amber" ? "bg-amber-400/30 text-amber-50 border-amber-400/50"
    : accent === "red" ? "bg-red-500/30 text-red-50 border-red-400/50"
    : accent === "fuchsia" ? "bg-fuchsia-500/30 text-fuchsia-50 border-fuchsia-400/50"
    : "bg-white text-black border-white";
  const passive = "bg-white/8 text-white/70 border-white/15";
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full border ${active ? accentBg : passive} transition`}
    >
      {label}
      <span className="ml-1.5 opacity-80 tabular-nums">{count}</span>
    </button>
  );
}
